import { showToast } from "@/api/notification";
import BaseUrl from "@/BaseUrl";
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

interface AuthResponse {
  user: { email: string; name: string };
  token: string;
  refreshToken: string;
}

interface LoginCredentials {
  username: string;
  password: string;
}

interface UserState {
  user: { email: string; name: string } | null;
  loading: boolean;
  error: string | null;
  token: string | null;
  refreshToken: string | null;
  isAuth: boolean;
}

const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
  token: localStorage.getItem("authToken"),
  refreshToken: localStorage.getItem("refreshToken"),
  isAuth: !!localStorage.getItem("authToken"),
};

export const loginUser = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: string }
>("login/loginUser", async (credentials, { rejectWithValue }) => {
  try {
    const response = await fetch(`${BaseUrl()}/api/auth/v1/auth/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      let errorMsg = "Login failed";
      try {
        const errorData = await response.json();
        errorMsg =
          errorData?.message ||
          errorData?.detail ||
          JSON.stringify(errorData) ||
          errorMsg;
      } catch (e) {
        errorMsg = response.statusText || errorMsg;
      }
      return rejectWithValue(errorMsg);
    }

    const rawData = await response.json();

    if (
      rawData &&
      rawData.data &&
      rawData.data.tokens &&
      rawData.data.tokens.access &&
      rawData.data.tokens.refresh
    ) {
      const accessToken = rawData.data.tokens.access;
      const refreshToken = rawData.data.tokens.refresh;
      const user = rawData.data.user;

      localStorage.setItem("authToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      showToast({ message: rawData.message, type: "success" });

      return {
        user: user,
        token: accessToken,
        refreshToken: refreshToken,
      };
    } else {
      return rejectWithValue(
        "Login succeeded but received unexpected data format."
      );
    }
  } catch (error: any) {
    showToast({ message: error.message, type: "error" });
    console.error("Login API call failed:", error);
    return rejectWithValue(
      error.message || "An unexpected error occurred during login"
    );
  }
});

export const refreshAuthToken = createAsyncThunk<
  { token: string },
  void,
  { rejectValue: string; state: { login: UserState } }
>("login/refreshAuthToken", async (_, { getState, rejectWithValue }) => {
  const currentRefreshToken = getState().login.refreshToken;
  if (!currentRefreshToken) {
    return rejectWithValue("No refresh token available");
  }

  try {
    const response = await fetch("/api/refresh-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: currentRefreshToken }),
    });

    if (!response.ok) {
      let errorMsg = "Token refresh failed";
      try {
        const errorData = await response.json();
        errorMsg =
          errorData?.message ||
          errorData?.detail ||
          JSON.stringify(errorData) ||
          errorMsg;
      } catch (e) {
        errorMsg = response.statusText || errorMsg;
      }

      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      return rejectWithValue(errorMsg);
    }

    const data: { access: string } = await response.json();
    if (data && data.access) {
      localStorage.setItem("authToken", data.access);
      return { token: data.access };
    } else {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      return rejectWithValue(
        "Refresh succeeded but received unexpected data format."
      );
    }
  } catch (error: any) {
    console.error("Token refresh API call failed:", error);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    return rejectWithValue(
      error.message || "An unexpected error occurred during token refresh"
    );
  }
});

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    logout(state) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuth = false;
      state.loading = false;
      state.error = null;
    },

    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isAuth = false;
      })
      .addCase(
        loginUser.fulfilled,
        (state, action: PayloadAction<AuthResponse>) => {
          state.loading = false;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuth = true;
          state.error = null;
        }
      )
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuth = false;
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        if (state.error) {
          showToast({ message: state.error, type: "error" });
        }
      })

      .addCase(refreshAuthToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        refreshAuthToken.fulfilled,
        (state, action: PayloadAction<{ token: string }>) => {
          state.loading = false;
          state.token = action.payload.token;
          state.isAuth = true;
          state.error = null;
        }
      )
      .addCase(refreshAuthToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Session expired. Please log in again.";

        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuth = false;
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
      });
  },
});

export const { logout, clearError } = loginSlice.actions;
export default loginSlice.reducer;
